/**
 * Launch mode — a SINGLE reversible switch to limit the live product surface
 * to "charts + CRM" for the initial go-live, while keeping ALL other code,
 * routes, and data fully intact.
 *
 *   NEXT_PUBLIC_LAUNCH_MODE=charts_crm   → limited surface (see below)
 *   (unset, or =full)                    → the whole product, nothing hidden
 *
 * When limited, the visible surface is:
 *   - Client (portal): /dashboard · /terminal · /stocks · /watchlist · /alerts · /account
 *   - Staff (analyst): /analyst/crm (+ /leads, /referrals, metrics, tasks)
 * Everything else is removed from the nav AND redirected to its section home
 * (a hidden portal route → /dashboard; a hidden analyst route → /analyst/crm).
 *
 * NOTHING IS DELETED. To bring the full product back: set
 * NEXT_PUBLIC_LAUNCH_MODE=full (or delete the var) and restart/redeploy.
 * It's a NEXT_PUBLIC_ var so both the middleware (server) and the nav
 * (client) read the same value; it is inlined at build time, so a change
 * needs a dev-server restart / redeploy to take effect.
 *
 * See docs/LAUNCH_MODE.md for the full hidden/kept list and restore steps.
 */

export const LAUNCH_MODE = process.env.NEXT_PUBLIC_LAUNCH_MODE ?? 'full';
export const LAUNCH_LIMITED = LAUNCH_MODE === 'charts_crm';

/** Client (portal) route prefixes hidden in charts_crm mode. */
export const PORTAL_HIDDEN_PREFIXES = [
  '/recommendations',
  '/portfolio', // also covers /portfolio/analysis
  '/paper',
  '/referrals',
  '/briefings',
  '/tax',
  '/reports',
  '/track-record',
  '/chat',
  '/agents',
  '/earnings',
  '/sentiment',
  '/progress',
  '/publications', // member research buckets — full product; hidden in charts_crm
] as const;

/** Staff (analyst) route prefixes hidden in charts_crm mode. Only the CRM
 * cluster (/analyst/crm, /analyst/leads, /analyst/referrals) stays visible. */
export const ANALYST_HIDDEN_PREFIXES = [
  '/analyst/console',
  '/analyst/queue',
  '/analyst/research',
  '/analyst/comms',
  '/analyst/backtest',
  '/analyst/automations',
  '/analyst/performance',
  '/analyst/methodology',
  '/analyst/audit',
  '/analyst/lifecycle',
  '/analyst/cost',
  '/analyst/webhooks',
  '/analyst/compliance',
] as const;

const ALL_HIDDEN_PREFIXES: readonly string[] = [
  ...PORTAL_HIDDEN_PREFIXES,
  ...ANALYST_HIDDEN_PREFIXES,
];

/** True iff `pathname` is hidden under the active launch mode. */
export function isHiddenPath(pathname: string): boolean {
  if (!LAUNCH_LIMITED) return false;
  return ALL_HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** Where to send someone who lands on a hidden route. */
export function launchSectionHome(pathname: string): string {
  return pathname.startsWith('/analyst') ? '/analyst/crm' : '/dashboard';
}

/** True iff a nav item with this href should be shown under the active mode. */
export function isNavVisible(href: string): boolean {
  return !isHiddenPath(href);
}
